import { useCallback, useEffect, useRef } from 'react'
import clsx from 'clsx'
import {
  DISCUSSION_LIMITS,
  GEAR_LOCKED_LINE,
  brandRowLine,
  canAddGear,
  brandRowsFromHits,
  gearFromBrand,
  gearFromHit,
  gearKey,
  hitSubline,
  linkableHit,
  type DraftGear,
} from 'lib/community/discussions-writer'
import { brandedTitle } from 'lib/dealhunt/url'
import type { DealHuntSuggestHit } from 'lib/dealhunt/types'
import { useOwnTypeahead } from 'lib/owner-notes/own-typeahead'
import base from 'components/community/community.module.css'
import styles from './Writer.module.css'

interface GearPanelProps {
  gear: DraftGear[]
  onAdd: (gear: DraftGear) => void
  onRemove: (key: string) => void
  /** Editing an answered post: the chips show without ✕ and the search gives way to
   *  `GEAR_LOCKED_LINE` (DealHunt refuses a gear change with 409 `gear_locked`). */
  locked?: boolean
  /** The phone's Link gear sheet: no card or heading (the sheet has its title), the linked chips
   *  first, the field focused, and ids of its own (the side card stays mounted, hidden). */
  inSheet?: boolean
}

const ResultBody: React.VFC<{ name: string; sub: string; imageUrl: string | null }> = ({
  name,
  sub,
  imageUrl,
}) => (
  <>
    {imageUrl ? (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        className={styles.resultThumb}
        src={imageUrl}
        alt=""
        width={28}
        height={28}
        loading="lazy"
        decoding="async"
      />
    ) : (
      <span className={styles.resultThumb} aria-hidden="true" />
    )}
    <span className={styles.resultText}>
      <span className={styles.resultName}>{name}</span>
      {sub && <span className={styles.resultSub}>{sub}</span>}
    </span>
    <span className={styles.resultAdd} aria-hidden="true">
      {'Add'}
    </span>
  </>
)

/**
 * LINK GEAR (design 10b): the site's product search (`useOwnTypeahead`, the browser Meili index)
 * narrowed to linkable audio product lines, then each distinct brand among those hits. Inside a
 * side card, so the results sit in flow and every row is two lines (Directive cbb347c7). The
 * product rows are exactly the typeahead's results in order, so the arrow keys and Enter walk
 * what is shown (Constraint 1b7f4658); the brand rows come after them, pointer and Tab only.
 * At DealHunt's 5 (`gear_max`) the search gives way to a one-line hint; unlinking one brings it back.
 */
const GearPanel: React.VFC<GearPanelProps> = ({
  gear,
  onAdd,
  onRemove,
  locked = false,
  inSheet = false,
}) => {
  const onPick = useCallback(
    (hit: DealHuntSuggestHit) => {
      const picked = gearFromHit(hit)
      if (picked) {
        onAdd(picked)
      }
    },
    [onAdd]
  )
  const keep = useCallback((hit: DealHuntSuggestHit) => linkableHit(hit, gear), [gear])
  const {
    query,
    setQuery,
    setOpen,
    loading,
    results,
    highlight,
    setHighlight,
    showDropdown,
    rootRef,
    onKeyDown,
    select,
  } = useOwnTypeahead(onPick, undefined, keep)
  const full = !canAddGear({ gear })
  const brands = showDropdown && !full ? brandRowsFromHits(results, gear) : []
  // In the sheet the field takes focus after the sheet's first frame (a tap leaves focus on the
  // row that opened it; BottomSheet's own focus is off for this sheet).
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (!inSheet) {
      return undefined
    }
    const frame = window.requestAnimationFrame(() =>
      inputRef.current?.focus({ preventScroll: true })
    )
    return () => window.cancelAnimationFrame(frame)
  }, [inSheet])
  const listId = inSheet ? 'writer-gear-sheet-results' : 'writer-gear-results'
  const helper = (
    <p className={base.text}>
      {locked
        ? GEAR_LOCKED_LINE
        : 'Owners of linked products get notified, and your post shows on the product page.'}
    </p>
  )

  const body = (
    <>
      {!inSheet && helper}
      {gear.length > 0 && (
        <ul className={styles.chips} aria-label="Linked gear">
          {gear.map(g => (
            <li key={gearKey(g)} className={styles.chip}>
              {g.kind === 'product' && g.image_url ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  className={styles.chipThumb}
                  src={g.image_url}
                  alt=""
                  width={20}
                  height={20}
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <span className={styles.chipThumb} aria-hidden="true" />
              )}
              <span className={styles.chipName}>{g.name}</span>
              {g.kind === 'brand' && <span className={styles.chipSub}>{'brand'}</span>}
              {!locked && (
                <button
                  type="button"
                  className={styles.chipRemove}
                  aria-label={`Unlink ${g.name}`}
                  onClick={() => onRemove(gearKey(g))}
                >
                  {'✕'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {inSheet && helper}
      {!locked && full && (
        <p className={styles.gearFull}>
          {`You can link up to ${DISCUSSION_LIMITS.gear} products or brands — unlink one to add another.`}
        </p>
      )}
      {!locked && !full && (
        <div ref={rootRef} className={styles.search}>
          <input
            className={styles.searchInput}
            type="search"
            value={query}
            placeholder="Search a brand or product"
            aria-label="Search a brand or product"
            role="combobox"
            aria-expanded={showDropdown && results.length > 0}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={highlight >= 0 ? `${listId}-${highlight}` : undefined}
            autoComplete="off"
            onChange={e => {
              setQuery(e.target.value)
              setOpen(true)
            }}
            ref={inputRef}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
          />
          {showDropdown && (
            <div className={styles.results}>
              {results.length > 0 && (
                <div id={listId} role="listbox" className={styles.resultList}>
                  {results.map((hit, i) => (
                    <button
                      key={hit.objectID}
                      id={`${listId}-${i}`}
                      type="button"
                      role="option"
                      aria-selected={i === highlight}
                      className={clsx(styles.result, { [styles.resultOn]: i === highlight })}
                      onMouseEnter={() => setHighlight(i)}
                      onClick={() => select(hit)}
                    >
                      <ResultBody
                        name={brandedTitle(hit.brand, hit.canonicalName || hit.title)}
                        sub={hitSubline(hit)}
                        imageUrl={hit.cloudinaryImageUrl || hit.imageUrl}
                      />
                    </button>
                  ))}
                </div>
              )}
              {brands.length > 0 && (
                <div role="group" aria-label="Brands" className={styles.resultList}>
                  {brands.map(row => (
                    <button
                      key={row.name}
                      type="button"
                      className={styles.result}
                      onClick={() => {
                        setQuery('')
                        setOpen(false)
                        onAdd(gearFromBrand(row))
                      }}
                    >
                      <ResultBody name={row.name} sub={brandRowLine(row)} imageUrl={null} />
                    </button>
                  ))}
                </div>
              )}
              {!results.length && (
                <p className={styles.noResults}>
                  {loading ? 'Searching…' : 'No match. Try a brand name.'}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </>
  )

  if (inSheet) {
    return <div className={styles.sheetGear}>{body}</div>
  }
  return (
    <section className={base.card} aria-labelledby="writer-gear">
      <div className={base.cardHead}>
        <h2 id="writer-gear" className={base.eyebrow}>
          {'Link gear'}
        </h2>
        <span className={styles.optional}>{locked ? 'Locked' : 'Optional'}</span>
      </div>
      {body}
    </section>
  )
}

export default GearPanel
